// src/frontend_react/src/pages/AdminDashboard/AdminOverviewPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';

// Base UI Components
import Card from '../../components/ui/Card/Card';
import Typography from '../../components/ui/Typography/Typography';
import Stack from '../../components/ui/Stack/Stack';
import Spinner from '../../components/ui/Spinner/Spinner';
import Alert from '../../components/ui/Alert/Alert';
import Table from '../../components/ui/Table/Table';
import Badge from '../../components/ui/Badge/Badge';
import Icon from '../../components/ui/Icon/Icon';
import Box from '../../components/ui/Box/Box';
import Tooltip from '../../components/ui/Tooltip/Tooltip';
import { HStack } from '../../components/ui/Stack/Stack';

// Admin Section Components
import UserManagementPage from './UserManagementPage';
import UserFormPage from './UserFormPage';
import ProjectManagementPage from './ProjectManagementPage';
import DataSourceTypeManagementPage from './DataSourceTypeManagementPage';
import RoleManagementPage from './RoleManagementPage';
import SecretManagementPage from './SecretManagementPage';
import HealthChecker from '../../components/shared/HealthChecker';
import MockDataGeneratorPage from './MockDataGeneratorPage';

// API
import { getAdminDashboardStats } from '../../services/api';

// Icons
import { FiGrid, FiUsers, FiBriefcase, FiActivity, FiClock, FiList } from 'react-icons/fi';

const DashboardStatCard = ({ title, value, icon, linkTo }) => (
    <Card flexGrow={1} className="admin-stat-card">
        <Link to={linkTo} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Stack p="md" spacing="sm" alignItems="center">
                <Icon as={icon} size="h2" color="secondary" />
                <Typography variant="h2" component="p" m={0}>{value}</Typography>
                <Typography variant="body1" color="secondary">{title}</Typography>
            </Stack>
        </Link>
    </Card>
);

const DashboardView = () => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getAdminDashboardStats();
            setStats(data);
        } catch (err) {
            setError(err.message || 'Could not load dashboard statistics.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const auditColumns = React.useMemo(() => [
        { Header: 'Time', accessor: 'timestamp', Cell: ({ value }) => new Date(value).toLocaleString() },
        { Header: 'User', accessor: 'user_username', Cell: ({value}) => value || 'System' },
        { Header: 'Action', accessor: 'action', Cell: ({ value }) => <Badge size="sm">{value}</Badge> },
        { Header: 'Details', accessor: 'details',
            Cell: ({ value }) => {
                if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
                return <Typography variant="caption" color="placeholder">N/A</Typography>;
                }
                // Convert object to a more readable string format
                const formattedDetails = Object.entries(value)
                .map(([key, val]) => `${key}: ${JSON.stringify(val)}`)
                .join('; ');
                return (
                <Tooltip content={formattedDetails} position="top">
                    <Typography variant="caption" component="code" color="secondary" noWrap={true} style={{maxWidth: '300px', display: 'inline-block'}}>
                    {formattedDetails}
                    </Typography>
                </Tooltip>
                );
            }
        },
    ], []);

    if (isLoading) {
        return <Spinner size="lg" />;
    }
    if (error) {
        return <Alert variant="error">{error}</Alert>;
    }

    return (
        <Stack spacing="xl">
            <Box>
                <Typography variant="h2" component="h1">Administrator Dashboard</Typography>
                <Typography color="secondary">An at-a-glance overview of platform activity and health.</Typography>
            </Box>

            <HStack spacing="lg" alignItems="stretch">
                <DashboardStatCard title="Active Users" value={stats?.user_stats?.active || 0} icon={FiUsers} linkTo="users" />
                <DashboardStatCard title="Active Projects" value={stats?.project_stats?.active || 0} icon={FiBriefcase} linkTo="projects" />
                <DashboardStatCard title="System Health" value="Online" icon={FiActivity} linkTo="health" />
            </HStack>

            <Card title="Recent Audit Events" titleIcon={<Icon as={FiClock} />}>
                <Table
                    columns={auditColumns}
                    data={stats?.recent_audit_events || []}
                    // --- New Empty State Props ---
                    emptyStateTitle="No Recent Audit Events"
                    emptyMessage="As actions are performed on the platform, they will be recorded here."
                    emptyStateIcon={FiList}
                />
            </Card>


            <HealthChecker isEmbedded />
        </Stack>
    );
};


function AdminOverviewPage() {
  return (
    <Stack spacing="xl">
      <Routes>
        <Route index element={<Navigate to="overview" replace />} /> 
        <Route path="overview" element={<DashboardView />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="users/create" element={<UserFormPage isEditMode={false} />} />
        <Route path="users/edit/:userId" element={<UserFormPage isEditMode={true} />} />
        <Route path="roles" element={<RoleManagementPage />} />
        <Route path="projects" element={<ProjectManagementPage />} />
        <Route path="datasourcetypes" element={<DataSourceTypeManagementPage />} />
        <Route path="secrets" element={<SecretManagementPage />} />
        <Route path="mock-data" element={<MockDataGeneratorPage />} />
        <Route path="health" element={
          <Card title="System Health Status">
            <HealthChecker showTitle={false} />
          </Card>
        } />
        <Route path="*" element={
          <Card title="Admin Section Not Found">
            <Typography variant="body1" color="secondary">
              The requested admin page does not exist.
            </Typography>
          </Card>
        } />
      </Routes>
    </Stack>
  );
}

export default AdminOverviewPage;